import { ISyncable } from "@app/common";
import { SyncSchedule } from "@app/core";
import { ProductCategory, TariffChangeType } from "@app/products-base";
import { Column, Display, Table } from "@messaia/cdk";
import { ContractStatus } from "../enums/contract-status";
import { SynchronizationPending } from "../enums/synchronization-pending";
import { SynchronizationStatus } from "../enums/synchronization-status";
import { ContractTermination } from "./contract-termination";
import { OrderItemBase } from "./order-item-base";

@Table({
	endpoint: 'orderitem',
	deletable: false,
	includes: [
		'OrderItemBundle.Order.Client',
		'OrderItemBundle.Order.Customer'
	]
})
export class OrderItem extends OrderItemBase implements ISyncable {
	@Column({ filterOperator: '~', width: 90 })
	@Display($localize`:@@number:Number`)
	number: number;

	@Column({ name: 'orderItemBundle.order.customer.firstName', filter: 'orderItemBundle.order.customer.firstName', showGtSm: true })
	@Display($localize`:@@firstName:First name`)
	private firstName: string;

	@Column({ name: 'orderItemBundle.order.customer.lastName', filter: 'orderItemBundle.order.customer.lastName' })
	@Display($localize`:@@lastName:Last name`)
	private lastName: string;

	@Column({ name: 'orderItemBundle.order.address.address1', filter: 'orderItemBundle.order.address.address1', showGtLg: true })
	@Display($localize`:@@address1:Address 1`)
	private address1: string;

	@Column({ name: 'orderItemBundle.order.address.address2', filter: 'orderItemBundle.order.address.address2', width: 60, showGtLg: true })
	@Display($localize`:@@address2:Address 2`)
	private address2: string;

	@Column({
		name: 'orderItemBundle.order.customer.billingAddress.address1',
		filter: 'orderItemBundle.order.customer.billingAddress.address1',
		content: (row: OrderItem) => `
            <div>${row.orderItemBundle?.order?.customer?.billingAddress?.address1} ${row.orderItemBundle?.order?.customer?.billingAddress?.address2}</div>
            <div>${row.orderItemBundle?.order?.customer?.billingAddress?.postalCode} ${row.orderItemBundle?.order?.customer?.billingAddress?.city}</div>`,
		showGtXl: true
	})
	@Display($localize`:@@billingAddress:Billing address`)
	private billingAddress: string;

	@Column({
		enumType: ContractStatus,
		cellNgClass: (row: OrderItem) => {
			return {
				'tc-orange-500': row.contractStatus == ContractStatus.New,
				'tc-green-500': row.contractStatus == ContractStatus.Active,
				'tc-black': row.contractStatus == ContractStatus.Cancelled,
				'tc-blue-500': row.contractStatus == ContractStatus.Terminated,
				'tc-red-500': row.contractStatus == ContractStatus.Completed
			}
		},
		showGtLg: true
	})
	@Display($localize`:@@contractStatus:Contract status`)
	contractStatus: ContractStatus;

	@Column({ name: 'orderItemBundle.order.tariffName', filter: 'orderItemBundle.order.tariffName', showGtLg: true })
	@Display($localize`:@@tariff:Tariff`)
	private tariff: string;

	@Column({ showGtMd: true })
	@Display($localize`:@@product:Product`)
	name: string;

	@Column({
		name: 'orderItemBundle.order.client.name',
		filter: 'orderItemBundle.order.clientId',
		endpoint: 'client?enabled=true&projection=id,name',
		maxWidth: 200,
		showGtXl: true
	})
	@Display($localize`:@@client:Client`)
	clientId: number;

	productCategory: ProductCategory;
	tariffChangeType: TariffChangeType;
	externalId: number;
	isReduced: boolean;
	reductionTimeLimit: number;
	external: boolean;
	statusId: number;
	tariffStatusId: number;
	provisionStatusId: number;
	synchronizationStatus: SynchronizationStatus;
	synchronizationPending: SynchronizationPending;
	canCancelOrder: boolean;
	canCancelContract: boolean;
	canRevokeContractCancellation: boolean;
	speedReduction: boolean;
	speedReduced: boolean;
	deliveryDate: Date | null;
	startDate: Date | null;
	endDate: Date | null;
	lastPossibleTerminationDate: Date | null;
	contractTermination: ContractTermination;
	cancelled: boolean;
	terminated: boolean;
	active: boolean;
	pending: boolean;
	syncSchedules: SyncSchedule[];
	statusSyncable: boolean;
	terminable: boolean;
	terminationRevocable: boolean;
}